import { themeQuartz } from "ag-grid-enterprise";
import { alpha } from "@mui/material/styles";
import { darkPalette, defaultPalette } from "../../utils/muiUtils";

// Density and framing, shared by both variants so the two never drift. Grids are the dominant
// surface in this app, so these few params carry more visual weight than any component override.
//   - 48px rows sit mid-range of the DS 8.4.6 window (44-56px for operational screens).
//   - Medium-weight 13px headers match DS 8.4 ("Inter Medium 12-14px") instead of Quartz's bold.
//   - The wrapper hairline + radius give a bare grid (pages that render one with no <Paper> around
//     it, e.g. Patient Data Management) a real surface of its own.
const gridDensity = {
    rowHeight: 48,
    headerHeight: 48,
    headerFontSize: 13,
    headerFontWeight: 500,
    cellHorizontalPadding: 16,
    wrapperBorderRadius: 12,
} as const;

// Quartz theme mapped to the 1health design system (Theming API — replaces the legacy
// ag-theme-alpine CSS). Reads defaultPalette so future palette changes carry into the
// grids automatically. Naming note: "1hGridTheme" is not a valid JS identifier (cannot
// start with a digit), hence oneHGridTheme.
export const oneHGridTheme = themeQuartz.withParams({
    browserColorScheme: "light", // native controls inside the grid (page-size select, date filters)
    accentColor: defaultPalette.primary.main, // Trust Blue — selection, focus, checkboxes
    foregroundColor: defaultPalette.softBlack.main, // Charcoal text
    borderColor: defaultPalette.silver.main, // Cloud hairlines
    headerBackgroundColor: defaultPalette.teal.light, // Clinical Ice header
    fontFamily: "'Inter', sans-serif",
    ...gridDensity,
    // Row hover signals clickability (DS 8.4.4). Kept to a 4% Trust Blue wash so it stays clearly
    // weaker than both the Clinical Ice header and the selected-row tint below.
    rowHoverColor: alpha(defaultPalette.primary.main, 0.04),
    selectedRowBackgroundColor: alpha(defaultPalette.primary.main, 0.1),
    // Muted header ink per DS 8.4 ("Slate/Mist"), so column labels sit *below* the row data in the
    // hierarchy rather than competing with it. 5.6:1 on the Clinical Ice header — AA.
    headerTextColor: defaultPalette.text.secondary,
});

// Dark variant — same semantic mapping over darkPalette. Grid mounts pick the variant from
// the active MUI theme mode (useTheme().palette.mode).
export const oneHGridDarkTheme = themeQuartz.withParams({
    browserColorScheme: "dark", // native controls inside the grid render dark variants
    accentColor: darkPalette.primary.main,
    foregroundColor: darkPalette.text.primary, // Cloud text
    backgroundColor: darkPalette.background.paper, // elevated Graphite
    borderColor: darkPalette.silver.main, // dark hairlines
    headerBackgroundColor: darkPalette.teal.light, // dark Clinical Ice header
    chromeBackgroundColor: darkPalette.softSilver.main, // toolbars/panels
    fontFamily: "'Inter', sans-serif",
    ...gridDensity,
    // Dark surfaces swallow a 4% wash, so both states step up — same relationship, more contrast.
    rowHoverColor: alpha(darkPalette.primary.main, 0.16),
    selectedRowBackgroundColor: alpha(darkPalette.primary.main, 0.28),
    headerTextColor: darkPalette.text.secondary, // Mist — 6.7:1 on the dark Clinical Ice header.
});
